// ============================================================================
// SignatureCapture — reusable signature input (Build S3, Task #19).
// ============================================================================
// Two methods, one component, used by FarmerSignaturesTab + ExternalSignPage:
//   * typed — the signer types their name EXACTLY as displayed (the API
//     enforces the match; we surface the requirement up front).
//   * drawn — canvas capture (mouse + touch), exported as PNG base64.
// ============================================================================

import { useRef, useState } from 'react';
import type { SignInput } from '../api';

interface Props {
  displayName: string;
  busy: boolean;
  onSubmit: (input: SignInput) => void;
}

export function SignatureCapture({ displayName, busy, onSubmit }: Props) {
  const [method, setMethod] = useState<'typed' | 'drawn'>('typed');
  const [typedName, setTypedName] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);

  function canvasPoint(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  function pointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    drawing.current = true;
    last.current = canvasPoint(e);
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
  }
  function pointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || !last.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    const pt = canvasPoint(e);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    last.current = pt;
    // Flip once — not on every pointer event (Copilot r2 render nit).
    if (!hasDrawn) setHasDrawn(true);
  }
  function pointerUp() {
    drawing.current = false;
    last.current = null;
  }
  function clearCanvas() {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d')!.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }

  function submit() {
    if (method === 'typed') {
      onSubmit({ method: 'typed', typed_name: typedName.trim() });
    } else {
      const dataUrl = canvasRef.current!.toDataURL('image/png');
      onSubmit({ method: 'drawn', drawn_png_base64: dataUrl.split(',')[1] });
    }
  }

  const canSubmit = !busy
    && (method === 'typed' ? typedName.trim() === displayName : hasDrawn);

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {(['typed', 'drawn'] as const).map((m) => (
          <button
            key={m} type="button" onClick={() => setMethod(m)}
            className={
              'px-3 py-1.5 rounded-md text-sm font-medium border ' +
              (method === m
                ? 'bg-uif-green text-white border-uif-green'
                : 'bg-white text-gray-700 border-gray-300 hover:border-uif-green/60')
            }
          >
            {m === 'typed' ? 'Type my name' : 'Draw my signature'}
          </button>
        ))}
      </div>

      {method === 'typed' ? (
        <div>
          <label className="block">
            <span className="block text-xs font-medium text-gray-700 mb-1">
              Type your name exactly as it appears: <span className="font-semibold">{displayName}</span>
            </span>
            <input
              type="text" value={typedName} onChange={(e) => setTypedName(e.target.value)}
              placeholder={displayName}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-lg italic focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              style={{ fontFamily: 'Georgia, serif' }}
            />
          </label>
          {typedName.trim() !== '' && typedName.trim() !== displayName && (
            <p className="mt-1 text-xs text-amber-700">The name must match exactly.</p>
          )}
        </div>
      ) : (
        <div>
          <canvas
            ref={canvasRef} width={560} height={160}
            onPointerDown={pointerDown} onPointerMove={pointerMove}
            onPointerUp={pointerUp} onPointerLeave={pointerUp}
            className="w-full border-2 border-dashed border-gray-300 rounded-md bg-white touch-none"
            style={{ touchAction: 'none' }}
          />
          <button type="button" onClick={clearCanvas} className="mt-1 text-xs text-gray-500 hover:text-gray-700 underline">
            Clear
          </button>
        </div>
      )}

      <button
        type="button" disabled={!canSubmit} onClick={submit}
        className="w-full rounded-md bg-uif-green text-white font-semibold px-4 py-2.5 disabled:opacity-40 hover:bg-uif-green-dark"
      >
        {busy ? 'Recording signature…' : 'Sign now'}
      </button>
      <p className="text-xs text-gray-500">
        By selecting "Sign now" you adopt this as your legal electronic signature.
      </p>
    </div>
  );
}
